import api from './api';
import { getFileNameFromContentDisposition } from '../utils/contentDisposition';
import { downloadBlob } from '../utils/downloadBlob';

export interface InformeParametro {
  name: string;
  type: string;
  required: boolean;
  allowMultiple?: boolean;
  promptText?: string | null;
  defaultValue?: unknown;
  allowedValues?: string[] | null;
}

export interface InformeStoreItem {
  id: number;
  codigo: string;
  modulo: string;
  titulo: string;
  descripcion?: string | null;
  planRequerido: string;
  instalado: boolean;
  versionPublicadaNumero?: number | null;
  parametrosCount: number;
}

export interface InformeStoreCatalogItem {
  id: number;
  codigo: string;
  modulo: string;
  titulo: string;
  descripcion?: string | null;
  planRequerido: string;
  versionNumero: number;
  versionFijada: boolean;
  parametrosCount: number;
}

export interface InformeStoreDetail extends InformeStoreCatalogItem {
  parametros: InformeParametro[];
}

async function parseBlobJsonError(err: unknown, fallbackMessage: string): Promise<never> {
  const axiosErr = err as { response?: { data?: Blob } };
  const data = axiosErr.response?.data;
  if (data instanceof Blob && (data.type.includes('json') || data.type.includes('text') || !data.type)) {
    const text = await data.text();
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string; errors?: Record<string, string[]> };
      const nested = parsed.errors
        ? Object.values(parsed.errors).flat().filter((m) => typeof m === 'string' && m.trim())
        : [];
      const detail = nested[0] || parsed.message || parsed.error || fallbackMessage;
      throw new Error(detail);
    } catch (inner) {
      if (inner instanceof Error && inner.message !== fallbackMessage && !inner.message.startsWith('Unexpected')) {
        throw inner;
      }
      throw new Error(text || fallbackMessage);
    }
  }
  throw err;
}

async function parsePdfError(err: unknown): Promise<never> {
  return parseBlobJsonError(err, 'No se pudo generar el PDF del informe.');
}

const BASE = '/api/informes-store';

const informesStoreService = {
  listStore: async (modulo = 'Ventas'): Promise<InformeStoreItem[]> => {
    const response = await api.get<InformeStoreItem[]>(BASE, { params: { modulo } });
    return Array.isArray(response.data) ? response.data : [];
  },

  pick: async (id: number): Promise<void> => {
    await api.post(`${BASE}/${id}/pick`);
  },

  unpick: async (id: number): Promise<void> => {
    await api.delete(`${BASE}/${id}/pick`);
  },

  listMisInformes: async (modulo = 'Ventas'): Promise<InformeStoreCatalogItem[]> => {
    const response = await api.get<InformeStoreCatalogItem[]>(`${BASE}/mis-informes`, { params: { modulo } });
    return Array.isArray(response.data) ? response.data : [];
  },

  getMisInforme: async (id: number): Promise<InformeStoreDetail> => {
    const response = await api.get<InformeStoreDetail>(`${BASE}/mis-informes/${id}`);
    return response.data;
  },

  renderPdf: async (id: number, parametros?: Record<string, unknown>): Promise<Blob> => {
    try {
      const response = await api.post(`${BASE}/mis-informes/${id}/render`, { parametros: parametros || {} }, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (err: unknown) {
      return parsePdfError(err);
    }
  },

  downloadArchivo: async (id: number, fallbackFileName = 'informe.rdlc'): Promise<void> => {
    try {
      const response = await api.get(`${BASE}/mis-informes/${id}/archivo`, {
        responseType: 'blob',
      });
      const cd = response.headers['content-disposition'] as string | undefined;
      const fileName = getFileNameFromContentDisposition(cd) || fallbackFileName;
      downloadBlob(response.data as Blob, fileName);
    } catch (err: unknown) {
      return parseBlobJsonError(err, 'No se pudo descargar el archivo del informe.');
    }
  },
};

export { parsePdfError };
export default informesStoreService;
